import React from 'react';
import { motion } from 'framer-motion';

const ClubPageSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 -mt-16 transition-colors duration-300">
      {/* Header Section */}
      <div className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 border-b border-gray-100 dark:border-gray-800 pt-20 transition-colors duration-300">
        <div className="max-w-6xl mx-auto px-4 pb-6">
          {/* Club Name Skeleton */}
          <motion.div
            className="flex justify-center mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg w-80 animate-pulse transition-colors duration-300" />
          </motion.div>

          {/* Venue Cards Skeleton */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map((index) => (
              <motion.div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm transition-colors duration-300"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 + index * 0.1 }}
              >
                {/* Map Skeleton */}
                <div className="relative h-48 bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 animate-pulse transition-colors duration-300" />

                {/* Info Section Skeleton */}
                <div className="p-3">
                  <div className="mb-3">
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-2 animate-pulse transition-colors duration-300" />
                    <div className="h-4 bg-gray-100 dark:bg-gray-600 rounded w-56 animate-pulse transition-colors duration-300" />
                  </div>

                  {/* Action Buttons Skeleton */}
                  <div className="flex gap-2">
                    <div className="flex-1 h-9 bg-gray-100 dark:bg-gray-600 rounded-md animate-pulse transition-colors duration-300" />
                    <div className="h-9 w-9 bg-gray-100 dark:bg-gray-600 rounded-md animate-pulse transition-colors duration-300" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Tab Navigation Skeleton */}
          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <div className="inline-flex rounded-full bg-gray-100 dark:bg-gray-800 p-1 gap-1 transition-colors duration-300">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-10 w-28 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse transition-colors duration-300" />
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Content Section Skeleton */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <motion.div
          className="space-y-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 p-4 transition-colors duration-300"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-2 animate-pulse transition-colors duration-300" />
                  <div className="h-4 bg-gray-100 dark:bg-gray-600 rounded w-32 animate-pulse transition-colors duration-300" />
                </div>
                <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse transition-colors duration-300" />
              </div>
            </div>
          ))}
        </motion.div>

        {/* Loading indicator */}
        <motion.div
          className="text-center mt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
            <motion.div
              className="w-1.5 h-1.5 bg-[#1C7AD6] dark:bg-[#3b9eff] rounded-full transition-colors duration-300"
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.5, 1, 0.5],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <span>Loading club details...</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ClubPageSkeleton;
